import { useCallback, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { DefaultButton } from "@fluentui/react/lib/Button";
import { Stack } from "@fluentui/react/lib/Stack";

import { IconButtonWithTooltip } from "Components/Generic/IconButtonWithTooltip";
import { WithTheme } from "Components/Generic/Theme/WithTheme";
import { WithTranslator } from "Components/Generic/Translator";
import { IUser } from "Model/Storage/IUser";
import { removeElementAt } from "Utils/Array";
import { IEditFieldProps } from "../IEditFieldProps";
import { useEditUserStyles } from "./UseEditUserStyles";
import { User } from "./User";

export const EditUser = WithTheme(
  WithTranslator((props: IEditFieldProps): JSX.Element => {
    const {
      item,
      fieldName,
      onChange,
      translator,
      disabled,
      theme: { spacing }
    } = props;
    const styles = useEditUserStyles();
    const [userList, setUserList] = useState<IUser[]>((item?.[fieldName] as IUser[]) ?? []);
    const [newUser, setNewUser] = useState<IUser>(null);
    const { accounts } = useMsal();
    const myDisplayName = accounts?.[0]?.name ?? `Unknown User`;
    const myUsername = accounts?.[0]?.username ?? `Unknown User`;

    const showAddMe = !userList.some(({ name }) => name === myUsername);

    const onAddMe = useCallback(() => {
      const updatedUserList = [...userList, { name: myUsername, displayName: myDisplayName }];
      setUserList(updatedUserList);
      onChange(fieldName, updatedUserList);
    }, [fieldName, myDisplayName, myUsername, onChange, userList]);

    const onUserChange = useCallback(
      (name: string, displayName: string, index: number) => {
        const updatedUserList = userList.map((user, currentIndex) => {
          if (index !== currentIndex) {
            return user;
          }

          return {
            name,
            displayName
          };
        });

        setUserList(updatedUserList);
        onChange(fieldName, updatedUserList);
      },
      [fieldName, onChange, userList]
    );

    const onNewUserChange = useCallback((username: string, displayName: string) => {
      setNewUser({ name: username, displayName });
    }, []);

    const onAdd = useCallback(() => {
      if (newUser == null) {
        return;
      }

      const updatedUserList = [...userList, { name: newUser?.name?.trim(), displayName: newUser?.displayName?.trim() }];
      setUserList(updatedUserList);
      setNewUser(null);
      onChange(fieldName, updatedUserList);
    }, [fieldName, newUser, onChange, userList]);

    const onRemove = useCallback(
      (index) => {
        const updatedUserList = removeElementAt(userList, index);
        setUserList(updatedUserList);
        onChange(fieldName, updatedUserList);
      },
      [fieldName, onChange, userList]
    );

    return (
      <Stack tokens={{ childrenGap: spacing.s1 }}>
        {userList.map((user, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Stack key={user.name} grow horizontal tokens={{ childrenGap: spacing.s2 }}>
            <User
              user={user}
              onChange={(n: string, dN: string) => onUserChange(n, dN, i)}
              disableAfterExport={disabled}
            />
            <Stack.Item disableShrink>
              <IconButtonWithTooltip
                iconProps={{ iconName: `Trash` }}
                tooltip={translator.get(`generic.remove`, `Remove`)}
                onClick={() => onRemove(i)}
                disabled={disabled}
              />
            </Stack.Item>
          </Stack>
        ))}
        <Stack grow horizontal tokens={{ childrenGap: spacing.s2 }}>
          <User user={newUser} onChange={onNewUserChange} disableAfterExport={disabled} />
          <Stack.Item disableShrink>
            <IconButtonWithTooltip
              iconProps={{ iconName: `Add` }}
              tooltip={translator.get(`generic.add`, `Add`)}
              onClick={onAdd}
              disabled={disabled}
            />
          </Stack.Item>
        </Stack>
        <Stack grow horizontal tokens={{ childrenGap: spacing.s2 }}>
          <Stack grow horizontal tokens={{ childrenGap: spacing.s2 }}>
            <Stack.Item grow>&nbsp;</Stack.Item>
            <Stack.Item grow align="end">
              {showAddMe && (
                <DefaultButton iconProps={{ iconName: `AddFriend` }} onClick={onAddMe} disabled={disabled}>
                  {translator.get(`edit.@users.addself`, `Add me`)}
                </DefaultButton>
              )}
            </Stack.Item>
          </Stack>
          <Stack.Item disableShrink className={styles.iconSpacer}>
            &nbsp;
          </Stack.Item>
        </Stack>
      </Stack>
    );
  })
);
